import { useState } from "react";
import { Plus, Wrench, Loader2, Pencil, Trash2, Check } from "lucide-react";
import toast from "react-hot-toast";
import { useMaintenance } from "../hooks/useMaintenance";
import MaintenanceFormModal from "./MaintenanceFormModal";

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function dueStatus(record) {
  if (record.status === "completed")
    return { label: `Completed ${fmtDate(record.completedAt)}`, color: "#16A34A", bg: "#F0FDF4" };
  if (!record.dueDate)
    return { label: "No due date set", color: "#6B7280", bg: "#F3F4F6" };
  const days = Math.ceil((new Date(record.dueDate).getTime() - Date.now()) / 86400000);
  if (days < 0)   return { label: `Overdue by ${Math.abs(days)}d`, color: "#DC2626", bg: "#FEF2F2" };
  if (days <= 7)  return { label: `Due in ${days}d`,               color: "#D97706", bg: "#FFFBEB" };
  return { label: `Due ${fmtDate(record.dueDate)}`,                 color: "#2563EB", bg: "#EFF6FF" };
}

export default function MaintenancePanel({ vehicles }) {
  const { records, loading, addRecord, updateRecord, completeRecord, removeRecord } = useMaintenance();
  const [showForm,      setShowForm]      = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  function openAdd()  { setEditingRecord(null); setShowForm(true); }
  function openEdit(r) { setEditingRecord(r);   setShowForm(true); }

  function vehicleLabel(vehicleId) {
    const v = vehicles.find(v => v.id === vehicleId);
    return v ? (v.nickname || v.plateNumber) : "Unknown vehicle";
  }

  async function handleSubmit(payload) {
    if (editingRecord) await updateRecord(editingRecord.id, payload);
    else                await addRecord(payload);
  }

  async function handleComplete(record) {
    try {
      await completeRecord(record.id);
      toast.success("Marked as completed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update record");
    }
  }

  async function handleDelete(record) {
    if (!window.confirm(`Remove this ${record.serviceType} record?`)) return;
    try {
      await removeRecord(record.id);
      toast.success("Maintenance record removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove record");
    }
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Maintenance</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>Track service schedules and history for your fleet</div>
          </div>
          <button
            onClick={openAdd}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#4F46E5", border: "none", borderRadius: 8,
              padding: "0 16px", height: 38, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            <Plus size={15} /> Schedule Service
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading maintenance records…
          </div>
        )}

        {!loading && records.length === 0 && (
          <div style={{
            background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No maintenance scheduled</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>Schedule your first service to start tracking due dates.</div>
            <button
              onClick={openAdd}
              style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
            >
              <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Schedule Service
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {records.map(record => {
            const status = dueStatus(record);
            return (
              <div key={record.id} style={{
                background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: "#EEF2FF", color: "#4F46E5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Wrench size={18} />
                </div>

                <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>
                    {record.serviceType} — {vehicleLabel(record.vehicleId)}
                  </div>
                  {record.dueOdometerKm && (
                    <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>Due at {record.dueOdometerKm.toLocaleString("en-IN")} km</div>
                  )}
                  <span style={{
                    display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 700,
                    color: status.color, background: status.bg, borderRadius: 99, padding: "2px 9px",
                  }}>
                    {status.label}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {record.status !== "completed" && (
                    <button
                      onClick={() => handleComplete(record)}
                      title="Mark completed"
                      style={{ background: "none", border: "1px solid #BBF7D0", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#16A34A", display: "flex", alignItems: "center", justifyContent: "center" }}
                    ><Check size={13} /></button>
                  )}
                  <button
                    onClick={() => openEdit(record)}
                    title="Edit record"
                    style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Pencil size={13} /></button>
                  <button
                    onClick={() => handleDelete(record)}
                    title="Remove record"
                    style={{ background: "none", border: "1px solid #FECACA", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <MaintenanceFormModal
          record={editingRecord}
          vehicles={vehicles}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}
    </main>
  );
}
